import axiosInstance from "../utils/axios";

const getData = (res, fallback = null) => res?.data?.data ?? fallback;

const mapReagentTypeToUi = (type = "") => {
  const value = String(type).toLowerCase();
  if (value === "sachet") return "sachet_powder";
  if (value === "liquid") return "liquid_bottle";
  return value;
};

const mapUiTypeToApi = (type = "") => {
  const value = String(type).toLowerCase();
  if (value === "sachet_powder") return "sachet";
  if (value === "liquid_bottle") return "liquid";
  return value;
};

const mapApiStatusToUi = (status = "") => String(status).toLowerCase().replace(/\s+/g, "_");

const mapUiStatusToApi = (status = "") => {
  const value = String(status).toLowerCase();
  if (value === "pending") return "Pending";
  if (value === "in_progress") return "In Progress";
  if (value === "completed") return "Completed";
  if (value === "shipped") return "Shipped";
  if (value === "cancelled") return "Cancelled";
  return status;
};

const mapUiSoilTestTypeToApi = (testType = "") => {
  const value = String(testType).toLowerCase();
  if (value === "all_params") return "all_parameters";
  if (value === "7_params") return "7_parameters";
  return testType;
};

const mapApiSoilTestTypeLegacy = (testType = "") => {
  const value = String(testType).toLowerCase();
  if (value === "all_parameters") return "all_params";
  if (value === "7_parameters") return "7_params";
  return testType;
};

const normalizePreparationMode = (mode) =>
  String(mode || "normal").toLowerCase() === "instant" ? "instant" : "normal";

export const normalizeReagent = (row) => ({
  ...row,
  type_id: mapReagentTypeToUi(row.type || row.type_id),
  reagent_type: { name: mapReagentTypeToUi(row.type || row.type_id) },
  preparation_mode: normalizePreparationMode(row.preparation_mode),
  compositions: Array.isArray(row.compositions) ? row.compositions : [],
});

export const normalizeOrder = (row) => ({
  ...row,
  type: row.order_type === "soil_test" ? "soil" : "custom",
  status: mapApiStatusToUi(row.status || ""),
  items: Array.isArray(row.items)
    ? row.items.map((item) => ({
        ...item,
        id: item.item_id,
        quantity: Number(item.quantity_required || 0),
        fulfilled_quantity: Number(item.quantity_fulfilled || 0),
        preparation_mode: normalizePreparationMode(item.preparation_mode),
      }))
    : [],
});

export const normalizeTarget = (row) => ({
  ...row,
  completed: Number(row.achieved_quantity || 0),
  target_date: row.target_date?.split("T")[0] || row.target_date,
});

export const normalizeWorkLog = (row) => ({
  ...row,
  work_date: row.log_date?.split("T")[0] || row.log_date,
  quantity_completed: Number(row.quantity_produced || 0),
});

export const normalizeInventoryHealth = (data = null) => {
  if (!data) return null;
  const summary = data.summary || {};
  return {
    items: Array.isArray(data.items) ? data.items : [],
    summary: {
      criticalCount: Number(summary.critical_count ?? summary.criticalCount ?? 0),
      warningCount: Number(summary.warning_count ?? summary.warningCount ?? 0),
      healthyCount: Number(summary.healthy_count ?? summary.healthyCount ?? 0),
    },
  };
};

export const normalizeLabDashboard = (data = {}) => ({
  stats: {
    totalReagents: Number(data?.stats?.reagents || 0),
    activeCrew: Number(data?.stats?.active_crew || 0),
    pendingOrders: Number(data?.stats?.orders || 0),
    lowStockItems: Number(data?.stats?.low_stock || 0),
    testCapacity: Number(data?.stats?.test_capacity || 0),
    hardware_sets_capacity: Number(data?.stats?.hardware_sets_capacity || 0),
  },
  teamProgress: {
    totalTarget: Number(data?.team_progress?.total_target || 0),
    totalAchieved: Number(data?.team_progress?.total_achieved || 0),
    percentage: Number(data?.team_progress?.percentage || 0),
  },
  crewPerformance: Array.isArray(data?.crew_performance) ? data.crew_performance : [],
  upcomingOrders: Array.isArray(data?.upcoming_orders) ? data.upcoming_orders : [],
});

export const labApi = {
  // Crew
  getCrew: async () => (getData(await axiosInstance.get("/lab/crew"), []) || []),
  getAvailableAdmins: async () => (getData(await axiosInstance.get("/lab/crew/available"), []) || []),
  getActiveCrew: async () => (getData(await axiosInstance.get("/lab/crew/active"), []) || []),
  addCrewMember: async (payload) => getData(await axiosInstance.post("/lab/crew", payload)),
  updateCrewMember: async (id, payload) => getData(await axiosInstance.put(`/lab/crew/${id}`, payload)),
  deleteCrewMember: async (id) => axiosInstance.delete(`/lab/crew/${id}`),

  // Crew portal
  getMyPortal: async (date) => getData(await axiosInstance.get("/lab/my-portal", { params: { date } }), {}),
  clockIn: async (payload) => getData(await axiosInstance.post("/lab/my-portal/clock-in", payload)),
  clockOut: async (payload) => getData(await axiosInstance.put("/lab/my-portal/clock-out", payload)),
  logMyWork: async (payload) => getData(await axiosInstance.post("/lab/my-portal/work-logs", payload)),

  // Targets
  getTargetsByDate: async (date) =>
    ((getData(await axiosInstance.get("/lab/targets", { params: { date } }), []) || []).map(normalizeTarget)),
  createTarget: async (payload) => getData(await axiosInstance.post("/lab/targets", payload)),
  deleteTarget: async (id) => axiosInstance.delete(`/lab/targets/${id}`),

  // Work logs
  getWorkLogsByDate: async (date) =>
    ((getData(await axiosInstance.get("/lab/work-logs", { params: { date } }), []) || []).map(normalizeWorkLog)),
  createWorkLog: async (payload) => getData(await axiosInstance.post("/lab/work-logs", payload)),

  // Reagents / Stock
  getReagents: async () =>
    ((getData(await axiosInstance.get("/lab/reagents"), []) || []).map(normalizeReagent)),
  getReagentStats: async () => getData(await axiosInstance.get("/lab/reagents/stats"), {}),
  getLowStockReagents: async () => (getData(await axiosInstance.get("/lab/reagents/low-stock"), []) || []),
  createReagent: async (payload) =>
    getData(
      await axiosInstance.post("/lab/reagents", {
        ...payload,
        type: mapUiTypeToApi(payload.type || payload.type_id),
        preparation_mode: normalizePreparationMode(payload.preparation_mode),
      })
    ),
  updateReagent: async (id, payload) =>
    getData(
      await axiosInstance.put(`/lab/reagents/${id}`, {
        ...payload,
        type: mapUiTypeToApi(payload.type || payload.type_id),
        preparation_mode: normalizePreparationMode(payload.preparation_mode),
      })
    ),
  deleteReagent: async (id) => axiosInstance.delete(`/lab/reagents/${id}`),
  adjustReagentStock: async (id, action, amount) =>
    getData(await axiosInstance.put(`/lab/reagents/${id}/stock/adjust`, { action, amount })),
  addReagentComposition: async (id, payload) =>
    getData(await axiosInstance.post(`/lab/reagents/${id}/composition`, payload)),
  removeReagentComposition: async (id, chemicalId) =>
    axiosInstance.delete(`/lab/reagents/${id}/composition/${chemicalId}`),
  /** Calculator: uses GET + query (works when POST /lab/reagents/calculate is not deployed). Same handler accepts POST + JSON body. */
  calculateRequirements: async ({ test_count, reagent_ids }) =>
    getData(
      await axiosInstance.get("/lab/reagents/calculate", {
        params: {
          test_count,
          reagent_ids: Array.isArray(reagent_ids) ? reagent_ids.join(",") : String(reagent_ids || ""),
        },
      }),
      {}
    ),
  getTestCapacity: async () => getData(await axiosInstance.get("/lab/reagents/capacity"), {}),

  // Chemicals
  getChemicals: async () => (getData(await axiosInstance.get("/lab/chemicals"), []) || []),
  getChemicalStats: async () => getData(await axiosInstance.get("/lab/chemicals/stats"), {}),
  createChemical: async (payload) => getData(await axiosInstance.post("/lab/chemicals", payload)),
  updateChemical: async (id, payload) => getData(await axiosInstance.put(`/lab/chemicals/${id}`, payload)),
  deleteChemical: async (id) => axiosInstance.delete(`/lab/chemicals/${id}`),
  addChemicalBatch: async (id, payload) => getData(await axiosInstance.post(`/lab/chemicals/${id}/batches`, payload)),
  updateChemicalBatchStatus: async (id, batchId, status) =>
    getData(await axiosInstance.put(`/lab/chemicals/${id}/batches/${batchId}/status`, { status })),

  // Orders
  getOrders: async (params = {}) => {
    const apiParams = { ...params };
    if (apiParams.status) {
      apiParams.status = mapUiStatusToApi(apiParams.status);
    }
    return ((getData(await axiosInstance.get("/lab/orders", { params: apiParams }), []) || []).map(normalizeOrder));
  },
  createCustomOrder: async (payload) => getData(await axiosInstance.post("/lab/orders/custom", payload)),
  createSoilOrder: async (payload) => {
    const normalizedType = mapUiSoilTestTypeToApi(payload?.test_type);
    const requestBody = { ...payload, test_type: normalizedType };
    try {
      return getData(await axiosInstance.post("/lab/orders/soil-test", requestBody));
    } catch (error) {
      const message = String(error?.response?.data?.message || "").toLowerCase();
      const shouldRetryWithLegacyType =
        error?.response?.status === 400 && message.includes("invalid test type");
      if (!shouldRetryWithLegacyType) throw error;
      return getData(
        await axiosInstance.post("/lab/orders/soil-test", {
          ...payload,
          test_type: mapApiSoilTestTypeLegacy(normalizedType),
        })
      );
    }
  },
  updateOrderStatus: async (id, status) =>
    getData(await axiosInstance.put(`/lab/orders/${id}/status`, { status: mapUiStatusToApi(status) })),
  /** PUT /lab/orders/items/:itemId/fulfill — body: { quantity_fulfilled } */
  updateOrderItemFulfillment: async (itemId, quantity_fulfilled) => {
    const res = await axiosInstance.put(`/lab/orders/items/${itemId}/fulfill`, { quantity_fulfilled });
    return {
      item: res?.data?.data ?? null,
      progress: res?.data?.progress ?? null,
    };
  },
  updateOrderDetails: async (id, payload) => {
    const res = await axiosInstance.put(`/lab/orders/${id}`, payload);
    return res.data;
  },
  deleteOrder: async (id) => axiosInstance.delete(`/lab/orders/${id}`),

  // Lab Dashboard
  getLabDashboard: async (date) =>
    normalizeLabDashboard(getData(await axiosInstance.get("/lab/dashboard", { params: { date } }), {})),

  // New endpoints (with graceful fallback — returns null if endpoint not deployed)
  getInventoryHealth: async () => {
    try {
      return normalizeInventoryHealth(getData(await axiosInstance.get("/lab/inventory/health"), null));
    } catch {
      return null;
    }
  },
  getCapacityBottleneck: async () => {
    try {
      return getData(await axiosInstance.get("/lab/capacity/bottleneck"), null);
    } catch {
      return null;
    }
  },
};

export const hardwareApi = {
  getComponents: async (params = {}) => 
    (getData(await axiosInstance.get("/hardware/components", { params }), []) || []),
  getLowStock: async () => (getData(await axiosInstance.get("/hardware/components/low-stock"), []) || []),
  createComponent: async (payload) => getData(await axiosInstance.post("/hardware/components", payload)),
  updateComponent: async (id, payload) => getData(await axiosInstance.put(`/hardware/components/${id}`, payload)),
  deleteComponent: async (id) => axiosInstance.delete(`/hardware/components/${id}`),
  
  // Batches
  getBatches: async (id) => (getData(await axiosInstance.get(`/hardware/components/${id}/batches`), []) || []),
  addBatch: async (id, payload) => getData(await axiosInstance.post(`/hardware/components/${id}/batches`, payload)),
  updateBatchStatus: async (batchId, status) =>
    getData(await axiosInstance.put(`/hardware/components/batches/${batchId}/status`, { status })),
  
  // New Hardware methods
  getHardware: async () => (getData(await axiosInstance.get("/hardware"), []) || []),
  createHardware: async (payload) => getData(await axiosInstance.post("/hardware", payload)),
  updateHardware: async (id, payload) => getData(await axiosInstance.put(`/hardware/${id}`, payload)),
  deleteHardware: async (id) => axiosInstance.delete(`/hardware/${id}`),
  
  // Hardware BOM
  addBOM: async (id, payload) => getData(await axiosInstance.post(`/hardware/${id}/bom`, payload)),
  removeBOM: async (id, componentId) => axiosInstance.delete(`/hardware/${id}/bom/${componentId}`),
  getHardwareCapacity: async () => getData(await axiosInstance.get("/hardware/calculator/capacity"), {}),

  getOrders: async (params) => getData(await axiosInstance.get('/hardware/orders', { params }), []),
  createCustomOrder: async (payload) => getData(await axiosInstance.post('/hardware/orders/custom', payload)),
  createFullSetOrder: async (payload) => getData(await axiosInstance.post('/hardware/orders/full-set', payload)),
  
  // Fulfillment 
  updateOrderItemFulfillment: async (itemId, quantity_fulfilled) => {
    const res = await axiosInstance.put(`/hardware/orders/items/${itemId}/fulfill`, { quantity_fulfilled });
    return {
      item: res?.data?.data ?? null,
      progress: res?.data?.progress ?? null,
    };
  },
  updateOrderStatus: async (id, status) => getData(await axiosInstance.put(`/hardware/orders/${id}/status`, { status })),
  updateOrderDetails: async (id, payload) => {
    const res = await axiosInstance.put(`/hardware/orders/${id}`, payload);
    return res.data;
  },
  deleteOrder: async (id) => axiosInstance.delete(`/hardware/orders/${id}`),
};
